import type { Dayjs } from 'dayjs';
import type { Expense, NewExpense } from 'src/types/types';

import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import { DatePicker } from '@mui/x-date-pickers';
import CardHeader from '@mui/material/CardHeader';
import InputAdornment from '@mui/material/InputAdornment';
import { Checkbox, TextField, Typography, FormControlLabel } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useAppDispatch } from 'src/hooks';
import { createExpense } from 'src/data/api';
import useFileUploader from 'src/lib/useImageUploader';
import { addExpense } from 'src/features/expenseSlice/expenseSlice';

import { Upload } from 'src/components/upload';
import { toast } from 'src/components/snackbar';

type Props = {
  currentExpense?: Expense;
};
export function ExpenseEditForm({ currentExpense }: Props) {
  const router = useRouter();

  const [id, setId] = useState<string>();
  const [date, setDate] = useState<Dayjs>();
  const [name, setName] = useState('');
  const [price, setPrice] = useState<string>('0');
  const [quantity, setQuantity] = useState(0);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [hasMoms, setHasMoms] = useState(true);

  const { handleUpload, fileUrl, setFileUrl } = useFileUploader(
    'invoices',
    currentExpense?.expenseUrl ?? undefined
  );

  const openDetails = useBoolean(true);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (currentExpense) {
      setId(currentExpense.id);
      setDate(dayjs.unix(currentExpense.date));
      setName(currentExpense.name);
      setPrice(currentExpense.price.toString().replace('.', ','));
      setQuantity(currentExpense.quantity);
      setNote(currentExpense.note);
      setHasMoms(currentExpense.hasMoms);
    }
  }, [currentExpense]);

  const onSubmit = () => {
    setLoading(true);
    const parsedPrice = parseFloat(price.replace(',', '.'));
    const body: NewExpense = {
      id,
      name,
      quantity,
      price: parsedPrice,
      date: date?.unix() ?? 0,
      expenseUrl: fileUrl ?? null,
      note,
      hasMoms,
    };

    createExpense(body)
      .then((p) => {
        toast.success('Udgift blev oprettet');
        router.push(paths.admin.expense.root);
        dispatch(addExpense(p));
      })
      .catch(() => {
        toast.error('Der skete en fejl. Produktet kunne ikke oprettes');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderDetails = () => (
    <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: 3 }}>
      <CardHeader
        titleTypographyProps={{ variant: 'h6' }}
        title="Detaljer"
        subheader="Udfyld informationerne herunder"
        sx={{ mb: 2 }}
      />

      <Collapse in={openDetails.value}>
        <Divider sx={{ mb: 3 }} />

        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Produktnavn"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <DatePicker
              label="Dato"
              value={date}
              onChange={(value) => value && setDate(value)}
              format="DD.MM.YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Antal"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value))}
            />
            <TextField
              fullWidth
              label="Pris pr. stk"
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography color="text.secondary">kr</Typography>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Total"
              value={(Number(price.replace(',', '.')) * quantity || 0).toFixed(2)}
              disabled
            />
          </Stack>

          <TextField
            label="Note"
            multiline
            minRows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          <FormControlLabel
            control={<Checkbox checked={hasMoms} onChange={(e) => setHasMoms(e.target.checked)} />}
            label="Beregn moms"
          />

          <Stack spacing={1}>
            <Typography variant="subtitle2">Kvittering</Typography>
            <Upload
              value={fileUrl}
              multiple={false}
              onDrop={(file) => handleUpload(file[0])}
              onDelete={() => setFileUrl('')}
            />
          </Stack>
        </Stack>
      </Collapse>
    </Card>
  );

  const renderActions = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        mt: 3,
      }}
    >
      <Button
        variant="contained"
        size="large"
        onClick={onSubmit}
        disabled={loading}
        sx={{ minWidth: 180 }}
      >
        {loading ? 'Gemmer...' : currentExpense ? 'Gem ændringer' : 'Opret udgift'}
      </Button>
    </Box>
  );

  return (
    <Stack spacing={{ xs: 3, md: 5 }}>
      {renderDetails()}
      {renderActions()}
    </Stack>
  );
}
